import { cloneDeep, get, isArray, isObjectLike, set } from "lodash";
import {
  IFieldArray,
  IFieldConfigurationObject,
  IFieldGroup,
  IFormOptionsAPI,
  ITemplateOptions,
  ITemplateScope,
} from "AngularFormly";
import {
  RecordsUtilsService,
  vsName as RecordsUtilsServiceName,
} from "../../../../utils";

export interface IRecordRepeaterTemplateOptions extends ITemplateOptions {
  displayNameSingular: string;
  displayNamePlural?: string;
  allowAdd: boolean;
  allowAddBottom: boolean;
  showHeader: boolean;
  repeaterFields: IFieldArray;
}

type RowState = { fields: IFieldArray; formOptions: IFormOptionsAPI };

interface IRecordRepeaterScope extends ITemplateScope {
  rowState: RowState[];
  getFieldValue: () => unknown;
  addRow: (index?: number) => void;
  deleteRow: (index: number) => void;
  getTrackByValue: (row: unknown) => any;
}

export class RecordRepeaterController {
  static $inject = ["$scope", RecordsUtilsServiceName];

  private readonly rowState: RowState[];

  private get fieldValue(): unknown[] | undefined {
    return <unknown[]>get(this.$scope.model, this.$scope.options.key);
  }
  private set fieldValue(value: unknown[] | undefined) {
    set(this.$scope.model ?? {}, this.$scope.options.key, value);
  }

  private get templateOptions(): IRecordRepeaterTemplateOptions {
    return <IRecordRepeaterTemplateOptions>this.$scope.to;
  }

  constructor(
    private readonly $scope: IRecordRepeaterScope,
    private readonly recordUtils: RecordsUtilsService
  ) {
    const rowState = this.getRowState(this.fieldValue?.length ?? 0);
    this.rowState = $scope.rowState = rowState;
    $scope.getFieldValue = () => this.fieldValue;
    $scope.addRow = (index?: number) => this.addRow(index);
    $scope.deleteRow = (index: number) => this.deleteRow(index);
    $scope.getTrackByValue = (row) => this.getTrackByValue(row);

    this.assignIdsToModel(this.fieldValue ?? []);
  }

  addRow(model?: unknown) {
    model ??= {};
    const newRowValue = cloneDeep(model);
    this.recordUtils.stripIds(newRowValue);
    const fieldValue = this.fieldValue ?? (this.fieldValue = []);

    const newFields = this.buildRowState(fieldValue.length);

    this.rowState.push(newFields);
    const modelIndex = fieldValue.indexOf(model);

    fieldValue.splice(
      modelIndex < 0 ? fieldValue.length : modelIndex + 1,
      0,
      newRowValue
    );
  }

  private assignIdsToModel(values: any[]): any {
    values.forEach((v) => this.recordUtils.assignId(v));
  }

  getTrackByValue(row: any): number {
    return this.recordUtils.getRecordId(row)!;
  }

  deleteRow(model?: unknown) {
    if (!this.fieldValue) {
      return;
    }
    const index = this.fieldValue.indexOf(model);
    this.fieldValue.splice(index, 1);
    this.rowState.splice(this.rowState.length - 1, 1);
  }

  moveUp(model: unknown) {
    if (!this.fieldValue) {
      return;
    }
    const index = this.fieldValue.indexOf(model);
    if (index < 1) {
      return;
    }
    this.fieldValue.splice(index, 1);
    this.fieldValue.splice(index - 1, 0, model);
  }

  moveDown(model: unknown) {
    if (!this.fieldValue) {
      return;
    }
    const index = this.fieldValue.indexOf(model);
    if (index < 0 || index === this.fieldValue.length - 1) {
      return;
    }
    this.fieldValue.splice(index, 1);
    this.fieldValue.splice(index + 1, 0, model);
  }

  getRowIndex(model: unknown) {
    return this.fieldValue?.indexOf(model) ?? -1;
  }

  private getRowState(count: number): RowState[] {
    const formattedFields = Array.from({ length: count }).map((_, index) =>
      this.buildRowState(index)
    );
    return formattedFields;
  }

  private buildRowState(index: number): RowState {
    const fields = cloneDeep(this.templateOptions.repeaterFields);
    const formState = { getRepeaterController: () => this };
    this.assignNewFieldIds(fields, index);
    return { fields, formOptions: { formState } };
  }

  private assignNewFieldIds(fields: IFieldArray, index: number) {
    fields.forEach((field) => {
      const fieldGroup = (field as IFieldGroup)?.fieldGroup;
      if (fieldGroup) {
        this.assignNewFieldIds(fieldGroup, index);
        return; // fieldGroups don't need an ID
      }

      if (field.templateOptions && field.templateOptions.fields) {
        this.assignNewFieldIds(field.templateOptions.fields, index);
      }
      const fieldConfigurationObject = field as IFieldConfigurationObject;
      const name =
        fieldConfigurationObject.name || fieldConfigurationObject.key;
      fieldConfigurationObject.id =
        fieldConfigurationObject.name = `${this.$scope.id}[${index}].${name}`;
    });
  }
}
